/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState, ChangeEvent } from "react";
import { Auth } from "@aws-amplify/auth";
import { FormProps } from "aws-amplify-react/props";
import { Form, Button } from "semantic-ui-react";
import useForm from "react-hook-form";
import styles from "./LandingPage/LandingForm/LandingForm.module.css";
import cStyles from "../Components.module.css";
import { notify, codeValidator } from "./utils";
import { fieldContainer } from "./FieldContainer";
import { useTranslation } from "react-i18next";

const VerifyContact: React.FC<FormProps> = ({
  authData,
  authState = "",
  onStateChange,
  onAuthEvent,
  setLoading,
  loading,
  setMessage,
  hide = [],
}) => {
  interface Inputs {
    code: string;
  }
  const { register, handleSubmit, errors } = useForm<FormData>();
  const emptyInputs: Inputs = {
    code: "",
  };
  const { t } = useTranslation("common");

  const [inputs, setInputs] = useState<Inputs>(emptyInputs);
  const { changeState } = notify(authState, onAuthEvent, onStateChange);
  const [sendCode, setSendCode] = useState<boolean>(true);

  const validAuthStates = ["verifyContact"];
  if (!validAuthStates.includes(authState)) {
    return null;
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const attemptVerification = () => {
    if (!Auth || typeof Auth.verifyCurrentUserAttributeSubmit !== "function") {
      throw new Error(
        "No Auth module found, please ensure @aws-amplify/auth is imported"
      );
    }

    setMessage(null);
    setLoading(true);
    Auth.verifyCurrentUserAttributeSubmit("email", inputs.code)
      .then(() => {
        setInputs(emptyInputs);
        setMessage(t("can_log_in"));
        changeState("signIn");
      })
      .catch((err) => setMessage(err.message || err))
      .finally(() => setLoading(false));
  };

  const requestVerification = () => {
    if (!Auth || typeof Auth.verifyCurrentUserAttribute !== "function") {
      throw new Error(
        "No Auth module found, please ensure @aws-amplify/auth is imported"
      );
    }

    setMessage(null);
    setLoading(true);
    Auth.verifyCurrentUserAttribute("email")
      .then(() => {
        setMessage(t("code_sent"));
        setSendCode(false);
        setTimeout(() => {
          setMessage(null);
        }, 5000);
      })
      .catch((err) => setMessage(err.message || err))
      .finally(() => setLoading(false));
  };

  const requestVerificationView = () => {
    const { unverified } = authData;
    if (!unverified) {
      return null;
    }
    const { email } = unverified;
    if (!email) {
      return null;
    }

    return (
      <React.Fragment>
        <div className={styles.radioButtonWrapper}>
          <label className={styles.landingRdContainer}>
            {t("send_code_to", { email: unverified.email })}
            <input
              type="radio"
              checked={sendCode}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setMessage(null);
                setSendCode(!!event.target.checked);
              }}
            />
            <span className={styles.landingRdCheck}></span>
          </label>
        </div>

        <div className={styles.radioButtonWrapper}>
          <label className={styles.landingRdContainer}>
            {t("already_code")}
            <input
              id="alreadyHaveCodeInput"
              type="radio"
              checked={!sendCode}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setMessage(null);
                setSendCode(!event.target.checked);
              }}
            />
            <span className={styles.landingRdCheck}></span>
          </label>
        </div>

        {!sendCode && (
          <React.Fragment>
            <Form.Group inline className={styles.formField}>
              <Form.Field
                className={styles.landingFormField}
                required
                error={!!errors.code}
              >
                {fieldContainer(
                  { label: t("common:code"), errors },
                  {
                    autoFocus: true,
                    name: "code",
                    key: "code",
                    placeholder: t("common:enter_code"),
                    autoComplete: "off",
                    onChange: handleInputChange,
                    ref: register({
                      required: t("code_required") as string,
                      pattern: {
                        value: codeValidator,
                        message: t("valid_code"),
                      },
                    }),
                  }
                )}
              </Form.Field>
            </Form.Group>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  };

  if (hide && hide.includes(VerifyContact)) {
    return null;
  }

  return (
    <React.Fragment>
      <Form
        className={styles.signupForm}
        onSubmit={handleSubmit(
          sendCode ? requestVerification : attemptVerification
        )}
      >
        <div className={styles.formHint}>{t("contact_required")}</div>

        {requestVerificationView()}

        <Button
          className={`${cStyles.lpButton} ${styles.submitFormButton}`}
          loading={loading}
          disabled={loading}
          id="sendCodeConfirmButton"
          onClick={() => {
            setMessage(null);
          }}
        >
          {sendCode ? t("send_code") : t("confirm")}
        </Button>
      </Form>
    </React.Fragment>
  );
};

export default VerifyContact;
